import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  EmployeeSelectorInitialValuesDocument,
  LegalEntityForSelectFragment,
  LegalEntitySelectorInitialValuesDocument,
} from 'generated/resources/graphql'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

export type Filters = {
  status?: string
  pendingApproval?: string
  category?: string
  subcategory?: string
  currency?: string
  employees?: EmployeeForSelectFragmentNoAccess[]
  legalEntities?: LegalEntityForSelectFragment[]
  creationType?: string
}

export type QuickFiltersContextType = {
  filters: Filters
  setFilters: (filters: Filters) => void
  clearFilters: () => void
}

const QuickFiltersContext = React.createContext<QuickFiltersContextType | null>({
  filters: {},
  setFilters: () => {},
  clearFilters: () => {},
})

export const QuickFiltersProvider = ({ children }: React.PropsWithChildren) => {
  const { company } = useCurrentSession()
  const [searchParams] = useSearchParams()
  const [employeesInitialized, setEmployeesInitialized] = useState(false)
  const [legalEntitiesInitialized, setLegalEntitiesInitialized] = useState(false)

  const initialEmployeeIds = searchParams.get('employeeIds')?.split(',') ?? []
  const initialLegalEntityIds = searchParams.get('legalEntityIds')?.split(',') ?? []

  const [filters, setFilters] = React.useState<Filters>({
    status: searchParams.get('status') ?? undefined,
    pendingApproval: searchParams.get('pending_approval') ?? undefined,
    category: searchParams.get('category') ?? undefined,
    subcategory: searchParams.get('subcategory') ?? undefined,
    currency: searchParams.get('currency') ?? undefined,
    creationType: searchParams.get('creationType') ?? undefined,
  })

  const clearFilters = () => {
    setFilters((prev) => ({
      ...prev,
      status: undefined,
      pendingApproval: undefined,
      category: undefined,
      subcategory: undefined,
      currency: undefined,
      creationType: undefined,
    }))
  }

  // Set initial employees if searchParams has employeeIds
  useQuery(EmployeeSelectorInitialValuesDocument, {
    variables: {
      companyId: company.id,
      ids: initialEmployeeIds,
    },
    skip: !initialEmployeeIds || initialEmployeeIds.length == 0 || employeesInitialized, // if employees is not undefined means this query has already done, so not need to repeat.
    onCompleted: (data) => {
      const employeeFragments = data.employees.employeeNames
      if (employeeFragments) {
        setFilters((prev) => ({ ...prev, employees: employeeFragments }))
        setEmployeesInitialized(true)
      }
    },
  })

  // Set initial legal entities if searchParams has legalEntityIds
  useQuery(LegalEntitySelectorInitialValuesDocument, {
    variables: {
      companyId: company.id,
      ids: initialLegalEntityIds,
    },
    skip: !initialLegalEntityIds || initialLegalEntityIds.length == 0 || legalEntitiesInitialized,
    onCompleted: (data) => {
      const legalEntityFragments = data.companies.legalEntities
      if (legalEntityFragments) {
        setFilters((prev) => ({ ...prev, legalEntities: legalEntityFragments }))
        setLegalEntitiesInitialized(true)
      }
    },
  })
  return (
    <QuickFiltersContext.Provider value={{ filters, setFilters, clearFilters }}>
      {children}
    </QuickFiltersContext.Provider>
  )
}

export const useQuickFiltersContext = () => {
  const context = React.useContext(QuickFiltersContext)
  if (!context) throw new Error('useQuickFiltersContext must be used within a QuickFiltersProvider')

  return context
}
